import type { Messages } from '../index';

/**
 * en-US permission labels. Shape is constrained by `Messages['permissions']`
 * (derived from the canonical German catalogue), so a literal labelled in one
 * locale and not the other is a `tsc` error — see `./de/permissions.ts` for why
 * this catalogue is shared rather than duplicated per app.
 */
export const permissions: Messages['permissions'] = {
  'workspace:identity:organization-create': 'Create Standort',
  'workspace:identity:organization-coverage-write': 'Manage coverage area',
  'workspace:identity:organization-coverage-read': 'Read coverage area',
  'workspace:identity:workspace-role-assign': 'Assign workspace roles',
  'workspace:identity:organizations-read': 'View Standorte',
  'workspace:identity:app-handoff-exchange': 'Exchange a handoff token',
  'workspace:identity:organization-read': 'View Standort',
  'workspace:identity:my-organizations-read': 'View own Standorte',
  'workspace:identity:profile-read': 'View own profile',
  'workspace:identity:profile-update': 'Edit own profile',
  'workspace:identity:preferences-read': 'View preferences',
  'workspace:identity:preferences-update': 'Edit preferences',
  'workspace:identity:member-read': 'View members',
  'workspace:identity:member-create': 'Add members',
  'workspace:identity:member-update': 'Edit members',
  'workspace:identity:member-profile-read': 'Read member profiles & portraits',
  'workspace:identity:member-profile-update': 'Edit member profiles',
  'workspace:identity:invitation-read': 'View invitations',
  'workspace:identity:invitation-resend': 'Resend invitations',
  'workspace:identity:invitation-revoke': 'Revoke invitations',
  'workspace:identity:company-read': 'View company details',
  'workspace:identity:company-update': 'Edit company details',
  'workspace:identity:branding-read': 'View branding',
  'workspace:identity:branding-update': 'Edit branding',
  'workspace:identity:document-template-read': 'View templates',
  'workspace:identity:document-template-create': 'Create templates',
  'workspace:identity:document-template-update': 'Edit templates',
  'workspace:identity:document-template-delete': 'Delete templates',
  'workspace:identity:email-template-read': 'View e-mail templates',
  'workspace:identity:email-template-create': 'Create e-mail templates',
  'workspace:identity:email-template-update': 'Edit e-mail templates',
  'workspace:identity:email-template-delete': 'Delete e-mail templates',
  'workspace:relationship:contact-read': 'View contacts',
  'workspace:relationship:company-read': 'View companies (CRM)',
  'workspace:relationship:contact-reassign': 'Reassign contacts to another Standort',
  'workspace:property:property-read': 'Read properties',
  'workspace:property:document-read': 'View property documents',
  'workspace:property:portal-connection-read': 'View the ImmoScout24 connection',
  'workspace:property:property-reassign': 'Reassign properties to another Standort',
  'workspace:deal:deal-read': 'Read deals',
  'workspace:deal:prospect-read': 'Read prospects',
  'workspace:deal:buyer-contract-template-read': 'View buyer brokerage contract templates',
  'workspace:deal:buyer-contract-template-create': 'Adapt the buyer brokerage contract template',
  'workspace:deal:buyer-contract-template-update': 'Edit the buyer brokerage contract template',
  'workspace:deal:buyer-contract-template-delete': 'Delete the buyer brokerage contract template',
  'workspace:deal:deal-reassign': 'Reassign deals to another Standort',
  'workspace:lead:lead-read': 'Read leads',
  'workspace:lead:lead-reassign': 'Reassign leads to another Standort',
  'workspace:assistant:conversation-use': 'Use the assistant',
  'workspace:assistant:conversation-read': 'View assistant history',
  'workspace:assistant:capability-gap-record': 'Record unsupported requests',
  'workspace:assistant:skill-manage': 'Manage agent skills',
  'workspace:assistant:skill-read': 'View agent skill runs',
  'workspace:communications:mailbox-connect': 'Connect mailbox',
  'workspace:communications:mailbox-disconnect': 'Disconnect mailbox',
  'workspace:communications:mailbox-read': 'Read other members’ mailboxes',
  'workspace:communications:company-mailbox-read': 'Read company mailboxes',
  'workspace:communications:company-mailbox-manage': 'Connect and disconnect company mailboxes',
  'workspace:communications:mailbox-thread-read': 'Read mailbox conversations',
  'workspace:communications:event-stream-read': 'Receive live workspace updates',
  'workspace:communications:calendar-read': 'Read own calendar',
  'workspace:communications:calendar-write': 'Manage appointments',
  'workspace:search:search-read': 'Search the workspace',
  'organization:identity:own-profile-read': 'View own profile',
  'organization:identity:organization-read': 'View Standort',
  'organization:identity:organization-update': 'Edit Standort',
  'organization:identity:member-read': 'View members',
  'organization:identity:location-member-read': 'View Standort members',
  'organization:identity:member-create': 'Add members',
  'organization:identity:member-update': 'Edit members',
  'organization:identity:member-role-assign': 'Assign member roles',
  'organization:identity:profile-read': 'View profile',
  'organization:identity:profile-update': 'Edit profile',
  'organization:identity:member-profile-read': 'View member profiles',
  'organization:identity:member-profile-update': 'Edit member profiles',
  'organization:identity:company-read': 'View company details',
  'organization:identity:company-update': 'Edit company details',
  'organization:identity:partner-onboard': 'Run partner onboarding',
  'organization:media:file-write': 'Upload files',
  'organization:media:file-read': 'View files',
  'organization:media:file-delete': 'Delete files',
  'organization:property:property-create': 'Create properties',
  'organization:property:property-read': 'Read property details',
  'organization:property:property-update': 'Edit properties',
  'organization:property:property-reassign': 'Reassign properties',
  'organization:property:property-delete': 'Delete properties',
  'organization:property:property-contact-write': 'Manage property contacts',
  'organization:property:document-read': 'Read documents',
  'organization:property:document-write': 'Manage property documents',
  'organization:property:document-request': 'Request documents',
  'organization:property:document-manage-any': 'Manage all property documents',
  'organization:property:media-read': 'Read images & videos',
  'organization:property:media-write': 'Upload property media',
  'organization:property:media-manage-any': 'Manage all property media',
  'organization:property:portal-listing-publish': 'Publish web exposés',
  'organization:property:portal-listing-withdraw': 'Withdraw web exposés',
  'organization:property:portal-listing-browser-publish':
    'Transmit properties to ImmoScout24 from the browser extension',
  'organization:deal:deal-create': 'Create deals',
  'organization:deal:deal-read': 'Read deal details',
  'organization:deal:deal-phase-advance': 'Advance deals',
  'organization:deal:deal-phase-move-back': 'Move deals back',
  'organization:deal:deal-update': 'Edit deals',
  'organization:deal:prospect-create': 'Create prospects',
  'organization:deal:prospect-phase-advance': 'Advance prospects',
  'organization:deal:prospect-phase-move-back': 'Move prospects back',
  'organization:deal:prospect-update': 'Edit prospects',
  'organization:deal:deal-delete': 'Delete deals',
  'organization:deal:deal-assign': 'Assign deals',
  'organization:deal:deal-oversight': 'Deal oversight (read every deal in the location)',
  'organization:deal:document-manage-any': 'Delete generated documents of any member',
  'organization:valuation:valuation-request-read': 'Read valuations',
  'organization:valuation:valuation-request-create': 'Create valuation requests',
  'organization:valuation:valuation-request-release': 'Release valuations',
  'organization:activity:activity-read': 'Read activities',
  'organization:activity:activity-update': 'Edit activities',
  'organization:activity:activity-write': 'Create activities',
  'organization:activity:activity-oversight': 'Activity oversight (read every activity in the location)',
  'organization:activity:activity-delete': "Delete other members' activities",
  'organization:relationship:contact-create': 'Create contacts',
  'organization:relationship:contact-read': 'View contacts',
  'organization:relationship:contact-update': 'Edit contacts',
  'organization:relationship:contact-delete': 'Delete contacts',
  'organization:relationship:contact-note-update': 'Edit contact notes',
  'organization:relationship:contact-note-delete': 'Delete contact notes',
  'organization:relationship:contact-communication-update': 'Edit contact communications',
  'organization:relationship:contact-communication-delete': 'Delete contact communications',
  'organization:relationship:company-create': 'Create companies',
  'organization:relationship:company-read': 'View companies',
  'organization:relationship:company-update': 'Edit companies',
  'organization:relationship:company-archive': 'Archive companies',
  'organization:relationship:company-unarchive': 'Restore companies',
  'organization:relationship:company-delete': 'Delete companies',
  'organization:relationship:company-contact-write': 'Manage company contacts',
  'organization:lead:lead-create': 'Create leads',
  'organization:lead:lead-read': 'Read lead details',
  'organization:lead:lead-update': 'Edit leads',
  'organization:lead:lead-assign': 'Assign leads',
  'organization:lead:lead-partner-read': 'View lead partners',
  'organization:lead:lead-partner-manage': 'Manage lead partners',
  'organization:communications:mailbox-send': 'Send emails',
  'organization:communications:appointment-write': 'Create appointments',
};
